/**
 * PrinterSettingsModal
 * Modal component for configuring fiscal printer connection URL
 */

import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';

import { FormRow } from '../../../app/components/FormRow';
import { usePrinterService } from '../../../common/hooks/usePrinterService';

interface PrinterSettingsModalProps {
  show: boolean;
  onHide: () => void;
  onConfigSaved?: () => void;
}

export const PrinterSettingsModal: React.FC<PrinterSettingsModalProps> = ({
  show,
  onHide,
  onConfigSaved,
}) => {
  const printerService = usePrinterService();
  const [apiUrl, setApiUrl] = useState<string>('');
  const [localError, setLocalError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (show) {
      const config = printerService.getConfig();
      setApiUrl(config?.apiUrl || '');
    }
    setLocalError(null);
  }, [show, printerService]);

  const validateUrl = (url: string): boolean => {
    if (!url) {
      setLocalError('Adres URL drukarki jest wymagany');
      return false;
    }

    try {
      new URL(url);
      return true;
    } catch {
      setLocalError(
        'Podaj prawidłowy adres URL (np. https://192.168.100.132:8888/api/v1)',
      );
      return false;
    }
  };

  const handleSave = async () => {
    setLocalError(null);

    if (!validateUrl(apiUrl)) {
      return;
    }

    setIsSaving(true);
    try {
      printerService.setConfig(apiUrl);
      await printerService.fetchAndStoreUniqueId();
      setLocalError(null);
      onConfigSaved?.();
      onHide();
    } catch (err) {
      const errorMsg =
        err instanceof Error
          ? err.message
          : 'Nie udało się zapisać konfiguracji';
      setLocalError(errorMsg);
    } finally {
      setIsSaving(false);
    }
  };

  const handleClear = () => {
    if (
      window.confirm('Czy na pewno chcesz wyczyścić konfigurację drukarki?')
    ) {
      printerService.clearConfig();
      setApiUrl('');
      setLocalError(null);
      onHide();
    }
  };

  const displayError = localError;

  return (
    <Modal show={show} onHide={onHide} centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Ustawienia drukarki fiskalnej</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {displayError && (
          <Form.Group className="mb-3">
            <div className="alert alert-danger" role="alert">
              {displayError}
              <button
                type="button"
                className="btn-close"
                onClick={() => setLocalError(null)}
                aria-label="Close"
              />
            </div>
          </Form.Group>
        )}

        <FormRow label="Adres API drukarki" controlId="printer-api-url">
          <Form.Control
            id="printer-api-url"
            type="url"
            placeholder="https://192.168.100.132:8888/api/v1"
            value={apiUrl}
            onChange={e => setApiUrl(e.target.value)}
            disabled={isSaving}
            required
          />
          <Form.Text className="text-muted">
            Np. https://192.168.100.132:8888/api/v1
          </Form.Text>
        </FormRow>
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="outline-danger"
          onClick={handleClear}
          disabled={isSaving || !apiUrl}
        >
          Wyczyść
        </Button>
        <Button variant="secondary" onClick={onHide} disabled={isSaving}>
          Anuluj
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={isSaving || !apiUrl}
        >
          {isSaving ? 'Zapisywanie...' : 'Zapisz'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
